<template lang="pug">
  b-modal(
    v-model="show",
    header-class="lmm-common-popup-header",
    :title="$t('device_location_map')",
    size="xl",
    body-class="p-0",
    hide-footer,
    @shown="onShown",
    @hidden="onHidden")
    .map-layout
      //- 좌: 온라인 기기 리스트 (검색 + 전체선택 + 다중 토글 체크리스트)
      .map-sidebar
        .map-search
          b-form-input(v-model="keyword", size="sm", :placeholder="$t('map_search_placeholder')")
        .map-dev-all
          input(type="checkbox", :checked="allChecked", @click="toggleAll")
          span.ml-2 {{ $t('map_select_all') }}
          span.ml-auto.text-muted {{ $t('map_shown_count', { count: shownCount }) }}
        .map-dev-list
          .map-dev(v-for="d in filteredDevices", :key="d.pk", @click="focusDevice(d)")
            input(type="checkbox", :checked="!!checked[d.pk]", @click.stop="toggleOne(d.pk)")
            span.ml-2.text-truncate {{ d.alias || d.pk }}
            span.st.ml-auto {{ osLabelOf(d) }}
          .map-dev.text-muted.justify-content-center(v-if="!filteredDevices.length") {{ $t('map_empty') }}
      //- 우: Leaflet 지도
      .map-area
        .map-canvas(ref="canvas")
</template>

<script>
import _ from "lodash"
import L from "leaflet"
import "leaflet/dist/leaflet.css"

// dev_os_id → 표시 라벨 (브랜드명이라 번역 안 함). MdmAssignModal과 동일 규칙.
const OS_LABEL = { 1: "Windows", 3: "macOS", 4: "Android" }

export default {
  name: "MdmDeviceMapModal",
  data() {
    return {
      show: false,
      keyword: "",
      devices: [], // 온라인(alive=1) + 좌표 있는 기기만
      checked: {}, // pk → bool (지도 표시 여부)
      map: null,
      markers: {}, // pk → L.marker
    }
  },
  computed: {
    // 검색 필터(이름 부분일치) — 없으면 전체
    filteredDevices() {
      const kw = this.keyword.trim().toLowerCase()
      if (!kw) return this.devices
      return this.devices.filter(d => String(d.alias || d.pk).toLowerCase().includes(kw))
    },
    // 지도에 표시(체크)된 대수
    shownCount() {
      return this.devices.filter(d => this.checked[d.pk]).length
    },
    // 필터된 리스트가 전부 체크됐나 (전체선택 체크박스 상태)
    allChecked() {
      return this.filteredDevices.length > 0 && this.filteredDevices.every(d => this.checked[d.pk])
    },
  },
  methods: {
    // dev_os_id(1=Win·3=Mac·4=Android) → 표시 라벨. 그 외는 빈 문자열
    osLabelOf(dev) {
      return OS_LABEL[dev.dev_os_id] || ""
    },
    // 기기 배열로 모달 열기 — 온라인 + 좌표 있는 것만 리스트에 담는다.
    // initialPks가 있으면 그 기기들만 초기 표시(단건=[pk]·벌크=[pk...]),
    // 없으면 전부 표시(하위호환). 좌측 리스트엔 언제나 전체가 남아 토글로 추가 가능.
    open(deviceList, initialPks = null) {
      this.devices = (deviceList || []).filter(
        d => Number(d.alive) === 1 && d.latitude != null && d.longitude != null,
      )
      this.checked = {}
      if (initialPks && initialPks.length) {
        // 지정 기기만 초기 체크 (devices에 실재하는 것만)
        const targets = new Set(initialPks.map(String))
        this.devices.forEach(d => this.$set(this.checked, d.pk, targets.has(String(d.pk))))
        // 지정 기기 중 좌표 있는 게 하나도 없으면 안내 (모달은 열되 마커 없음)
        if (!this.devices.some(d => this.checked[d.pk])) {
          this.$awn.warning(this.$t("map_empty"))
        }
      } else {
        // 인자 없이 열면 전체 표시
        this.devices.forEach(d => this.$set(this.checked, d.pk, true))
      }
      this.keyword = ""
      this.show = true
    },
    // 모달이 완전히 뜬 뒤 지도 초기화 (DOM·크기 준비 후). b-modal @shown
    onShown() {
      this.initMap()
      this.$nextTick(() => this.map && this.map.invalidateSize())
    },
    // 모달 닫힐 때 지도 파기 (재오픈 시 "Map container already initialized" 방지)
    onHidden() {
      if (this.map) {
        this.map.remove()
        this.map = null
      }
      this.markers = {}
    },
    // Leaflet 지도 생성 + OSM 타일 + 마커 렌더
    initMap() {
      if (this.map) this.map.remove()
      // 전역 id 대신 DOM 요소(ref) 직접 전달 — 충돌 없음
      this.map = L.map(this.$refs.canvas, { zoomControl: true }).setView([36.5, 127.8], 7)
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(this.map)
      this.renderMarkers()
    },
    // 체크된 기기만 마커 렌더
    renderMarkers() {
      if (!this.map) return
      Object.values(this.markers).forEach(m => this.map.removeLayer(m))
      this.markers = {}
      const pts = []
      this.devices.forEach(d => {
        if (!this.checked[d.pk]) return
        const lat = Number(d.latitude)
        const lng = Number(d.longitude)
        const icon = L.divIcon({
          className: "map-pin-icon",
          html: "📍",
          iconSize: [26, 26],
          iconAnchor: [13, 26],
          popupAnchor: [0, -24],
        })
        const label = _.escape(String(d.alias || d.pk))
        const marker = L.marker([lat, lng], { icon }).addTo(this.map)
        marker.bindPopup(`<b>${label}</b>`)
        // 이름 상시 표시 — 클릭 없이 한눈에 파악.
        marker.bindTooltip(label, {
          permanent: true,
          direction: "top",
          offset: [0, -22],
          className: "map-pin-label",
        })
        this.$set(this.markers, d.pk, marker)
        pts.push([lat, lng])
      })
      if (pts.length) this.map.fitBounds(pts, { padding: [40, 40], maxZoom: 15 })
    },
    // 개별 토글 → 마커 갱신
    toggleOne(pk) {
      this.$set(this.checked, pk, !this.checked[pk])
      this.renderMarkers()
    },
    // 전체 토글(현재 필터된 것 기준) → 마커 갱신
    toggleAll() {
      const target = !this.allChecked
      this.filteredDevices.forEach(d => this.$set(this.checked, d.pk, target))
      this.renderMarkers()
    },
    // 리스트 행 클릭 → 그 기기로 지도 포커스 + 팝업 (체크 꺼져 있으면 켜고 렌더)
    focusDevice(d) {
      if (!this.checked[d.pk]) {
        this.$set(this.checked, d.pk, true)
        this.renderMarkers()
      }
      if (!this.map) return
      this.map.setView([Number(d.latitude), Number(d.longitude)], 15)
      const m = this.markers[d.pk]
      if (m) m.openPopup()
    },
  },
}
</script>

<style scoped lang="scss">
.map-layout {
  display: grid;
  grid-template-columns: 262px minmax(0, 1fr);
  height: 580px;
}
.map-sidebar {
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.map-search {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}
.map-dev-all {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  cursor: pointer;
}
.map-dev-list {
  flex: 1;
  overflow: auto;
  padding: 4px 0;
}
.map-dev {
  display: flex;
  align-items: center;
  padding: 9px 14px;
  font-size: 13px;
  cursor: pointer;
}
.map-dev:hover {
  background: #f8f9fa;
}
.map-dev .st {
  font-size: 11px;
  color: #6b7280;
  padding-left: 6px;
}
.map-area {
  min-height: 0;
}
.map-canvas {
  width: 100%;
  height: 100%;
}
::v-deep .map-pin-icon {
  font-size: 22px;
  line-height: 1;
  text-align: center;
  background: transparent;
  border: 0;
}
// 마커 이름 라벨(상시 표시) — 개별 폭 제한 + 말줄임으로 겹침 완화
::v-deep .map-pin-label {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  padding: 1px 6px;
}
::v-deep .map-pin-label::before {
  display: none; // leaflet tooltip 기본 화살표 제거(핀과 겹쳐 지저분)
}
@media (max-width: 767px) {
  .map-layout {
    grid-template-columns: 1fr;
    height: auto;
  }
  .map-sidebar {
    border-right: 0;
    border-bottom: 1px solid #e5e7eb;
  }
  .map-dev-list {
    max-height: 150px;
  }
  .map-area {
    height: 340px;
  }
}
</style>
